import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import type { CalendarScale } from "./calendarModel";
import { useCalendarPinch } from "./useCalendarPinch";
import {
  periodEnd,
  periodKey,
  periodStart,
  periodWindow,
  shiftPeriod,
} from "./calendarStreamModel";

export function MobileCalendarStream({
  scale,
  date,
  onCurrentDate,
  onRange,
  renderPeriod,
  onZoom,
}: {
  scale: CalendarScale;
  date: Date;
  onCurrentDate: (date: Date) => void;
  onRange: (range: { scale: CalendarScale; start: Date; end: Date }) => void;
  renderPeriod: (date: Date) => ReactNode;
  onZoom?: (direction: 1 | -1, anchor?: Date) => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  useCalendarPinch(root, onZoom);
  const [center, setCenter] = useState(() => periodStart(date, scale));
  const periods = periodWindow(center, scale);
  const current = useRef(periodKey(date, scale));
  const centerRef = useRef(center);
  centerRef.current = center;
  const scrolling = useRef(false);
  const initialized = useRef(false);
  const correction = useRef<{ key: string; top: number } | null>(null);
  const pending = useRef(false);
  const lastScroll = useRef(0);
  const collapseAnchor = useRef<{ element: HTMLElement; top: number } | null>(
    null,
  );
  const readingAnchor = useRef<{
    key: string;
    top: number;
    scrollTop: number;
  } | null>(null);
  const callbacks = useRef({ onCurrentDate, onRange });
  callbacks.current = { onCurrentDate, onRange };
  const first = periods[0].getTime(),
    last = periods.at(-1)!.getTime();
  useLayoutEffect(() => {
    const saved = collapseAnchor.current;
    const node = root.current;
    if (!saved || !node || !saved.element.isConnected) return;
    node.scrollTop += saved.element.getBoundingClientRect().top - saved.top;
    lastScroll.current = node.scrollTop;
    const period = saved.element.closest<HTMLElement>("[data-period-key]");
    if (period) {
      current.current = period.dataset.periodKey!;
      readingAnchor.current = {
        key: current.current,
        top: period.getBoundingClientRect().top,
        scrollTop: node.scrollTop,
      };
    }
    collapseAnchor.current = null;
  }, [renderPeriod]);
  useEffect(() => {
    callbacks.current.onRange({
      scale,
      start: periodStart(new Date(first), scale),
      end: periodEnd(new Date(last), scale),
    });
  }, [first, last, scale]);
  useLayoutEffect(() => {
    const node = root.current;
    if (!node) return;
    if (!initialized.current) {
      const initial = node.querySelector<HTMLElement>(
        `[data-period-key="${periodKey(date, scale)}"]`,
      );
      if (initial) node.scrollTop = Math.max(0, initial.offsetTop - 80);
      initialized.current = true;
    } else if (correction.current) {
      const anchor = node.querySelector<HTMLElement>(
        `[data-period-key="${correction.current.key}"]`,
      );
      if (anchor)
        node.scrollTop +=
          anchor.getBoundingClientRect().top - correction.current.top;
    }
    const reading = node.querySelector<HTMLElement>(
      `[data-period-key="${current.current}"]`,
    );
    if (reading)
      readingAnchor.current = {
        key: current.current,
        top: reading.getBoundingClientRect().top,
        scrollTop: node.scrollTop,
      };
    lastScroll.current = node.scrollTop;
    correction.current = null;
    pending.current = false;
  }, [center, date, scale]);
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    let frame = 0;
    let idle: ReturnType<typeof setTimeout> | undefined;
    let input = false;
    let touching = false;
    let touchY = 0;
    let direction = 0;
    const visiblePeriod = () => {
      const elements = Array.from(
        node.querySelectorAll<HTMLElement>(":scope > [data-period-key]"),
      );
      const line = node.getBoundingClientRect().top + 92;
      const anchor =
        elements.find((item) => {
          const box = item.getBoundingClientRect();
          return box.top <= line && box.bottom > line;
        }) ??
        elements.find((item) => item.getBoundingClientRect().bottom > line);
      return { elements, anchor };
    };
    // Recycling changes scrollTop. Never treat that correction as another gesture,
    // and never replace periods while native touch momentum is still running.
    const refill = () => {
      scrolling.current = false;
      if (touching || !input || pending.current) return;
      input = false;
      const { elements, anchor } = visiblePeriod();
      if (!anchor) return;
      const nearTop = node.scrollTop < 180;
      const nearBottom =
        node.scrollTop + node.clientHeight > node.scrollHeight - 180;
      if (
        !(direction < 0 && (nearTop || elements.indexOf(anchor) === 0)) &&
        !(direction > 0 && (nearBottom || anchor === elements.at(-1)))
      )
        return;
      const nextCenter = shiftPeriod(centerRef.current, scale, direction);
      const next = periodWindow(nextCenter, scale);
      if (
        !next.length ||
        periodKey(next[0], scale) === elements[0]?.dataset.periodKey
      )
        return;
      correction.current = {
        key: anchor.dataset.periodKey!,
        top: anchor.getBoundingClientRect().top,
      };
      pending.current = true;
      setCenter(nextCenter);
    };
    const settle = () => {
      clearTimeout(idle);
      if (!touching && input) idle = setTimeout(refill, 180);
    };
    const measure = () => {
      frame = 0;
      if (pending.current) return;
      const { anchor } = visiblePeriod();
      if (!anchor) return;
      const key = anchor.dataset.periodKey!;
      readingAnchor.current = {
        key,
        top: anchor.getBoundingClientRect().top,
        scrollTop: node.scrollTop,
      };
      lastScroll.current = node.scrollTop;
      if (key !== current.current) {
        current.current = key;
        callbacks.current.onCurrentDate(
          new Date(Number(anchor.dataset.periodStamp)),
        );
      }
    };
    const scroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
      settle();
    };
    const wheel = (event: WheelEvent) => {
      if (!event.deltaY) return;
      direction = Math.sign(event.deltaY);
      input = true;
      scrolling.current = true;
      settle();
    };
    const start = (event: TouchEvent) => {
      touching = event.touches.length === 1;
      touchY = event.touches[0]?.clientY ?? 0;
      clearTimeout(idle);
    };
    const move = (event: TouchEvent) => {
      if (!touching || event.touches.length !== 1) return;
      const y = event.touches[0].clientY;
      if (Math.abs(y - touchY) > 2) {
        direction = y < touchY ? 1 : -1;
        input = true;
        scrolling.current = true;
        touchY = y;
      }
    };
    const end = () => {
      touching = false;
      settle();
    };
    const key = (event: KeyboardEvent) => {
      if (
        (event.target as HTMLElement).matches(
          'input,textarea,select,[role="combobox"]',
        )
      )
        return;
      if (
        ["ArrowDown", "PageDown", "End", "ArrowUp", "PageUp", "Home"].includes(
          event.key,
        )
      ) {
        direction = ["ArrowUp", "PageUp", "Home"].includes(event.key) ? -1 : 1;
        input = true;
        scrolling.current = true;
        settle();
      }
    };
    node.addEventListener("scroll", scroll, { passive: true });
    node.addEventListener("wheel", wheel, { passive: true });
    node.addEventListener("touchstart", start, { passive: true });
    node.addEventListener("touchmove", move, { passive: true });
    node.addEventListener("touchend", end, { passive: true });
    node.addEventListener("touchcancel", end, { passive: true });
    node.addEventListener("keydown", key);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(idle);
      scrolling.current = false;
      node.removeEventListener("scroll", scroll);
      node.removeEventListener("wheel", wheel);
      node.removeEventListener("touchstart", start);
      node.removeEventListener("touchmove", move);
      node.removeEventListener("touchend", end);
      node.removeEventListener("touchcancel", end);
      node.removeEventListener("keydown", key);
    };
  }, [scale]);
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      const saved = readingAnchor.current;
      if (
        !saved ||
        scrolling.current ||
        pending.current ||
        Math.abs(node.scrollTop - saved.scrollTop) > 1
      )
        return;
      const anchor = node.querySelector<HTMLElement>(
        `[data-period-key="${saved.key}"]`,
      );
      if (!anchor) return;
      const delta = anchor.getBoundingClientRect().top - saved.top;
      if (Math.abs(delta) > 1) {
        node.scrollTop += delta;
        lastScroll.current = node.scrollTop;
        readingAnchor.current = {
          key: saved.key,
          top: anchor.getBoundingClientRect().top,
          scrollTop: node.scrollTop,
        };
      }
    });
    node
      .querySelectorAll(":scope > section")
      .forEach((period) => observer.observe(period));
    return () => observer.disconnect();
  }, [first, last]);
  return (
    <div
      ref={root}
      onClickCapture={(event) => {
        const button = (event.target as HTMLElement).closest<HTMLElement>(
          "button[aria-expanded][aria-controls]",
        );
        const content = button?.getAttribute("aria-controls");
        if (
          button &&
          content &&
          document.getElementById(content)?.hasAttribute("data-mobile-collapse")
        ) {
          collapseAnchor.current = {
            element: button,
            top: button.getBoundingClientRect().top,
          };
        }
      }}
      className="crm-calendar-stream"
      data-calendar-stream={scale}
      role="region"
      aria-label={
        scale === "year"
          ? "Годы календаря"
          : scale === "month"
            ? "Месяцы календаря"
            : "Дни календаря"
      }
      tabIndex={0}
    >
      {periods.map((period) => (
        <StreamPeriod
          key={periodKey(period, scale)}
          stamp={period.getTime()}
          scale={scale}
          renderPeriod={renderPeriod}
        />
      ))}
    </div>
  );
}

const StreamPeriod = memo(function StreamPeriod({
  stamp,
  scale,
  renderPeriod,
}: {
  stamp: number;
  scale: CalendarScale;
  renderPeriod: (date: Date) => ReactNode;
}) {
  const period = new Date(stamp);
  return (
    <section
      className="crm-stream-period"
      data-scale={scale}
      aria-labelledby={`calendar-period-${scale}-${periodKey(period, scale)}`}
      data-period-key={periodKey(period, scale)}
      data-period-stamp={period.getTime()}
    >
      <h2
        id={`calendar-period-${scale}-${periodKey(period, scale)}`}
        className="crm-stream-title"
      >
        {format(
          period,
          scale === "year"
            ? "yyyy"
            : scale === "month"
              ? "LLLL yyyy"
              : "EEEE, d MMMM yyyy",
          { locale: ru },
        )}
      </h2>
      {renderPeriod(period)}
    </section>
  );
});
